import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  MagnifyingGlass,
  CalendarDots,
  ArrowCounterClockwise,
  CaretDown,
} from "@phosphor-icons/react";
import { THERAPY_STATUS_CONFIG, THERAPY_PAYMENT_CONFIG } from "@/lib/status-configs";
import { FilterChip } from "./FilterChip";
import type { TherapyFilters } from "./types";

const STATUS_CONFIG = THERAPY_STATUS_CONFIG;
const PAYMENT_CONFIG = THERAPY_PAYMENT_CONFIG;
const FILTER_STATUS_KEYS = ["scheduled", "completed", "cancelled", "no_show"] as const;

function fmtRangeDate(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${date.getFullYear()}.`;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysAgo(n: number): Date {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - n);
  return d;
}

interface TherapiesFilterBarProps {
  therapistSearch: string;
  debouncedTherapist: string;
  onSearchChange: (value: string) => void;
  filters: TherapyFilters;
  onFilterChange: <K extends keyof TherapyFilters>(key: K, value: TherapyFilters[K]) => void;
  onFiltersChange: (partial: Partial<TherapyFilters>) => void;
  onResetAll: () => void;
}

export function TherapiesFilterBar({
  therapistSearch,
  debouncedTherapist,
  onSearchChange,
  filters,
  onFilterChange,
  onFiltersChange,
  onResetAll,
}: TherapiesFilterBarProps) {
  const { dateFrom, dateTo, status, paymentStatus, payoutStatus } = filters;
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(undefined);

  const handleRangeOpenChange = (open: boolean) => {
    if (open) {
      setDraftRange(dateFrom || dateTo ? { from: dateFrom, to: dateTo } : undefined);
    }
    setRangeOpen(open);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (dateFrom || dateTo) count++;
    if (status) count++;
    if (paymentStatus) count++;
    if (payoutStatus) count++;
    if (debouncedTherapist) count++;
    return count;
  }, [dateFrom, dateTo, status, paymentStatus, payoutStatus, debouncedTherapist]);

  const rangeLabel = useMemo(() => {
    if (dateFrom && dateTo) return `${fmtRangeDate(dateFrom)} – ${fmtRangeDate(dateTo)}`;
    if (dateFrom) return `Od ${fmtRangeDate(dateFrom)}`;
    if (dateTo) return `Do ${fmtRangeDate(dateTo)}`;
    return "Sve vrijeme";
  }, [dateFrom, dateTo]);

  const applyDraft = () => {
    onFiltersChange({
      dateFrom: draftRange?.from,
      dateTo: draftRange?.to ?? draftRange?.from,
    });
    setRangeOpen(false);
  };

  const applyPreset = (from: Date | undefined, to: Date | undefined) => {
    onFiltersChange({ dateFrom: from, dateTo: to });
    setRangeOpen(false);
  };

  const draftLabel = useMemo(() => {
    if (draftRange?.from && draftRange?.to)
      return `${fmtRangeDate(draftRange.from)} – ${fmtRangeDate(draftRange.to)}`;
    if (draftRange?.from) return fmtRangeDate(draftRange.from);
    return "Nije odabrano";
  }, [draftRange]);

  const today = startOfDay(new Date());
  const presets: { label: string; from: Date | undefined; to: Date | undefined }[] = [
    { label: "Danas", from: today, to: today },
    { label: "7 dana", from: daysAgo(6), to: today },
    { label: "30 dana", from: daysAgo(29), to: today },
    {
      label: "Ovaj mjesec",
      from: new Date(today.getFullYear(), today.getMonth(), 1),
      to: today,
    },
    { label: "Sve vrijeme", from: undefined, to: undefined },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      {/* Search row */}
      <div className="flex items-center gap-1 border-b border-border/40 px-4">
        <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
        <Input
          placeholder="Pretraži po imenu klijenta ili terapeuta..."
          value={therapistSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-12 border-0 bg-transparent shadow-none focus-visible:ring-0"
        />
        {activeFilterCount > 0 && (
          <button
            onClick={onResetAll}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowCounterClockwise className="size-3" />
            Očisti ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Filter controls */}
      <div className="grid grid-cols-2 gap-2 px-4 py-3 sm:flex sm:flex-wrap sm:items-center">
        {/* Period */}
        <Popover open={rangeOpen} onOpenChange={handleRangeOpenChange}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "col-span-2 inline-flex h-9 items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 text-[13px] transition-colors hover:bg-muted/50",
                dateFrom || dateTo ? "border-primary/40 text-foreground" : "text-muted-foreground",
              )}
            >
              <span className="inline-flex items-center gap-2">
                <CalendarDots className="size-3.5 shrink-0" />
                <span className="whitespace-nowrap">{rangeLabel}</span>
              </span>
              <CaretDown className="size-3 shrink-0 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto max-w-[calc(100vw-2rem)] p-0">
            <div className="flex flex-wrap gap-1.5 border-b border-border/40 p-3">
              {presets.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p.from, p.to)}
                  className="rounded-full border border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="max-h-[min(420px,60vh)] overflow-y-auto">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={draftRange}
                onSelect={setDraftRange}
                defaultMonth={draftRange?.from ?? dateFrom ?? new Date()}
              />
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border/40 p-3">
              <span className="text-xs tabular-nums text-muted-foreground">{draftLabel}</span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setDraftRange(undefined)}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Poništi
                </button>
                <button
                  onClick={applyDraft}
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
                >
                  Primijeni
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Status */}
        <Select
          value={status || "all"}
          onValueChange={(v) => onFilterChange("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="h-9 w-full gap-1.5 rounded-lg text-[13px] sm:w-auto">
            <span className="text-muted-foreground">Status:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sve</SelectItem>
            {FILTER_STATUS_KEYS.map((key) => (
              <SelectItem key={key} value={key}>{STATUS_CONFIG[key].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Payment */}
        <Select
          value={paymentStatus || "all"}
          onValueChange={(v) => onFilterChange("paymentStatus", v === "all" ? "" : v)}
        >
          <SelectTrigger className="h-9 w-full gap-1.5 rounded-lg text-[13px] sm:w-auto">
            <span className="text-muted-foreground">Plaćanje:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sve</SelectItem>
            {Object.entries(PAYMENT_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Payout */}
        <Select
          value={payoutStatus || "all"}
          onValueChange={(v) => onFilterChange("payoutStatus", v === "all" ? "" : v)}
        >
          <SelectTrigger className="h-9 w-full gap-1.5 rounded-lg text-[13px] sm:w-auto">
            <span className="text-muted-foreground">Isplata:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sve</SelectItem>
            <SelectItem value="pending">Čeka isplatu</SelectItem>
            <SelectItem value="paid">Isplaćeno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border/40 px-4 py-2.5">
          {(dateFrom || dateTo) && (
            <FilterChip
              label={rangeLabel}
              onRemove={() => onFiltersChange({ dateFrom: undefined, dateTo: undefined })}
            />
          )}
          {status && (
            <FilterChip
              label={STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label ?? status}
              onRemove={() => onFilterChange("status", "")}
            />
          )}
          {paymentStatus && (
            <FilterChip
              label={PAYMENT_CONFIG[paymentStatus as keyof typeof PAYMENT_CONFIG]?.label ?? paymentStatus}
              onRemove={() => onFilterChange("paymentStatus", "")}
            />
          )}
          {payoutStatus && (
            <FilterChip
              label={payoutStatus === "paid" ? "Isplaćeno" : "Čeka isplatu"}
              onRemove={() => onFilterChange("payoutStatus", "")}
            />
          )}
          {debouncedTherapist && (
            <FilterChip
              label={`Pretraga: ${therapistSearch}`}
              onRemove={() => onSearchChange("")}
            />
          )}
        </div>
      )}
    </div>
  );
}
